import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { QuoteCardType } from 'components/QuoteCard';
import { mergeSuperDispatchPriceTrend } from 'components/SuperDispatch/buildSuperDispatchTrendChartData';
import {
  SuperDispatchInternalLaneContextResponse,
  SuperDispatchInternalLaneContextStatus,
  SuperDispatchQuoteMetadata,
} from 'lib/api/getQuickQuote';
import { TransportType } from 'types/QuoteRequest';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';

import { SelectedQuoteType } from '../types';
import { isRequestAborted, waitWithSignal } from '../utils/abortableWait';
import { isSuperDispatchLaneContextHardFailure } from '../utils/superDispatchLaneContext';

type StartPollParams = {
  generation: number;
  quoteId: Undef<number>;
  fetchLaneContext: (
    signal: AbortSignal
  ) => Promise<SuperDispatchInternalLaneContextResponse>;
};

enum PollOutcome {
  Ready = 'ready',
  Pending = 'pending',
  Failed = 'failed',
}

type PollResult =
  | {
      outcome: PollOutcome.Ready;
      response: SuperDispatchInternalLaneContextResponse;
    }
  | { outcome: PollOutcome.Pending | PollOutcome.Failed };

// A cold lane-data request can take 40-90 seconds. Limit the total wait instead
// of the request count because the server may hold each request for 15 seconds.
const POLL_MAX_MS = 120_000;
const POLL_INITIAL_DELAY_MS = 2_000;
const POLL_MAX_DELAY_MS = 10_000;

// Each submit gets a generation number. Responses from older generations are
// ignored so a slow request can't overwrite a newer quote.
export function useSuperDispatchLaneContextPoll({
  transportType,
  setQuoteCards,
}: {
  transportType: TransportType;
  setQuoteCards: Dispatch<SetStateAction<QuoteCardType[]>>;
}) {
  const generationRef = useRef(0);
  const abortControllerRef = useRef<Maybe<AbortController>>(null);

  const cancelPoll = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    generationRef.current += 1;
    return generationRef.current;
  }, []);

  const isGenerationCurrent = useCallback(
    (generation: number) => generation === generationRef.current,
    []
  );

  useEffect(
    () => () => {
      cancelPoll();
    },
    [cancelPoll]
  );

  // Stop loading lane data when the rep leaves auto transport. Switching
  // between other equipment types must not drop an in-flight quote.
  const previousTransportTypeRef = useRef(transportType);
  useEffect(() => {
    const previousTransportType = previousTransportTypeRef.current;
    previousTransportTypeRef.current = transportType;

    if (
      transportType === TransportType.AUTO ||
      previousTransportType !== TransportType.AUTO
    ) {
      return;
    }

    const wasPolling = abortControllerRef.current !== null;
    cancelPoll();
    if (wasPolling) {
      setQuoteCards(settlePendingSuperDispatchCards);
    }
  }, [transportType, cancelPoll, setQuoteCards]);

  const startPoll = useCallback(
    ({ generation, quoteId, fetchLaneContext }: StartPollParams) => {
      if (!isGenerationCurrent(generation)) {
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const isStale = () =>
        !isGenerationCurrent(generation) || abortController.signal.aborted;

      const patchCard = (patch: Partial<SuperDispatchQuoteMetadata>) => {
        if (isStale()) {
          return;
        }

        setQuoteCards((cards) =>
          cards.map((card) => {
            if (
              card.type !== SelectedQuoteType.SUPER_DISPATCH ||
              card.quoteId !== quoteId
            ) {
              return card;
            }

            return {
              ...card,
              superDispatchMetadata: {
                ...card.superDispatchMetadata,
                ...patch,
              },
            };
          })
        );
      };

      const run = async () => {
        const deadline = Date.now() + POLL_MAX_MS;
        let delayMs = POLL_INITIAL_DELAY_MS;

        while (!isStale()) {
          const laneContext = await fetchLaneContextOnce(
            fetchLaneContext,
            abortController.signal
          );
          if (isStale()) {
            return;
          }

          if (laneContext.outcome === PollOutcome.Ready) {
            if (isStale()) {
              return;
            }

            setQuoteCards((cards) =>
              cards.map((card) => {
                if (
                  card.type !== SelectedQuoteType.SUPER_DISPATCH ||
                  card.quoteId !== quoteId
                ) {
                  return card;
                }

                return {
                  ...card,
                  superDispatchMetadata: {
                    ...card.superDispatchMetadata,
                    ...readyPatch(
                      laneContext.response,
                      card.superDispatchMetadata
                    ),
                  },
                };
              })
            );
            return;
          }

          if (laneContext.outcome === PollOutcome.Failed) {
            break;
          }

          const remainingMs = deadline - Date.now();
          if (remainingMs <= 0) {
            break;
          }

          try {
            await waitWithSignal(
              Math.min(delayMs, remainingMs),
              abortController.signal
            );
          } catch {
            return;
          }
          delayMs = Math.min(delayMs * 2, POLL_MAX_DELAY_MS);
        }

        patchCard({
          internalLaneContextStatus:
            SuperDispatchInternalLaneContextStatus.Unavailable,
        });
      };

      void run().finally(() => {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      });
    },
    [isGenerationCurrent, setQuoteCards]
  );

  return { cancelPoll, isGenerationCurrent, startPoll };
}

// Mark cards unavailable when their poll was canceled, so they don't keep
// showing loading skeletons.
export function settlePendingSuperDispatchCards(
  cards: QuoteCardType[]
): QuoteCardType[] {
  return cards.map((card) => {
    if (
      card.type !== SelectedQuoteType.SUPER_DISPATCH ||
      card.superDispatchMetadata?.internalLaneContextStatus !==
        SuperDispatchInternalLaneContextStatus.Pending
    ) {
      return card;
    }

    return {
      ...card,
      superDispatchMetadata: {
        ...card.superDispatchMetadata,
        internalLaneContextStatus:
          SuperDispatchInternalLaneContextStatus.Unavailable,
      },
    };
  });
}

// Retry transient errors. Stop when the route is missing or the server returns
// a final status.
async function fetchLaneContextOnce(
  fetchLaneContext: StartPollParams['fetchLaneContext'],
  signal: AbortSignal
): Promise<PollResult> {
  try {
    const response = await fetchLaneContext(signal);
    if (response.status === SuperDispatchInternalLaneContextStatus.Ready) {
      return { outcome: PollOutcome.Ready, response };
    }

    if (response.status === SuperDispatchInternalLaneContextStatus.Pending) {
      return { outcome: PollOutcome.Pending };
    }

    return { outcome: PollOutcome.Failed };
  } catch (error) {
    if (
      isRequestAborted(error) ||
      isSuperDispatchLaneContextHardFailure(error)
    ) {
      return { outcome: PollOutcome.Failed };
    }

    return { outcome: PollOutcome.Pending };
  }
}

// The backend omits empty arrays. A ready response without postings or trend
// means none were found. Recent moves come with the quote, so replace them only
// when this response includes them.
function readyPatch(
  laneContext: SuperDispatchInternalLaneContextResponse,
  existing: MaybeUndef<Partial<SuperDispatchQuoteMetadata>>
): Partial<SuperDispatchQuoteMetadata> {
  const priceTrend = mergeSuperDispatchPriceTrend(
    existing?.priceTrend,
    laneContext.priceTrend
  );

  return {
    internalLaneContextStatus: SuperDispatchInternalLaneContextStatus.Ready,
    recentPostings: laneContext.recentPostings ?? [],
    ...(priceTrend !== undefined && { priceTrend }),
    ...(laneContext.recentMoves && { recentMoves: laneContext.recentMoves }),
    ...(laneContext.externalUrl && { externalUrl: laneContext.externalUrl }),
  };
}
